import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';

const items = [
  { 
    label: 'EOB Posting', 
    icon: '📄', 
    description: 'Automated explanation of benefits processing',
    link: '/healthcare/ai/payment-posting'
  },
  { 
    label: 'Insurance Eligibility Verification', 
    icon: '🛡️', 
    description: 'Real-time insurance verification',
    link: '/healthcare/ai/eligibility-check'
  },
  { 
    label: 'Insurance Follow-ups', 
    icon: '👩‍💼', 
    description: 'Automated claim status tracking',
    link: '/healthcare/ai/accounts-receivable'
  },
  { 
    label: 'Patient Intake', 
    icon: '🧾', 
    description: 'Streamlined patient registration',
    link: '/healthcare/ai/patient-intake'
  },
  { 
    label: 'Physician Notifications', 
    icon: '🩺', 
    description: 'Instant provider alerts',
    link: '/healthcare/ai'
  },
  { 
    label: 'Claim Corrections', 
    icon: '📝', 
    description: 'AI-powered error detection',
    link: '/healthcare/ai/denial-management'
  },
  { 
    label: 'Smart Reminders', 
    icon: '🔔', 
    description: 'Automated appointment reminders',
    link: '/healthcare/ai'
  },
  { 
    label: 'Automated Appeals', 
    icon: '🧠', 
    description: 'Intelligent denial management',
    link: '/healthcare/ai/denial-management'
  },
  { 
    label: 'Copay Analysis', 
    icon: '💸', 
    description: 'Patient payment optimization',
    link: '/healthcare/ai'
  },
  { 
    label: 'Patient Statements', 
    icon: '📬', 
    description: 'Automated billing statements',
    link: '/healthcare/ai'
  },
];

const PatientExperienceCarousel = () => {
  // ✅ ItemList Schema for Services
  const itemListSchema = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "name": "AI-Powered Medical Billing Services",
    "description": "Comprehensive list of automated revenue cycle management services powered by AI",
    "numberOfItems": items.length,
    "itemListElement": items.map((item, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "item": {
        "@type": "Service",
        "name": item.label,
        "description": item.description,
        "url": `https://mdercm.com${item.link}`
      }
    }))
  };

  // ✅ Service Catalog Schema WITH ADDRESS
  const serviceCatalogSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "serviceType": "AI-Powered Medical Billing Automation",
    "provider": {
      "@type": "MedicalBusiness",
      "name": "MDeRCM",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "Five Greentree Centre, 525 Route 73 North, Suite 104",
        "addressLocality": "Marlton",
        "addressRegion": "NJ",
        "postalCode": "08053",
        "addressCountry": "US"
      }
    },
    "areaServed": "United States",
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Healthcare RCM Automation Services",
      "itemListElement": items.slice(0, 5).map(item => ({
        "@type": "Offer",
        "itemOffered": {
          "@type": "Service",
          "name": item.label,
          "description": item.description
        }
      }))
    }
  };

  return (
    <section
      style={{
        background: '#fff',
        padding: '80px 5%',
        textAlign: 'center',
        fontFamily: 'Poppins, sans-serif',
        overflow: 'hidden',
        position: 'relative',
        zIndex: 1,
      }}
      itemScope
      itemType="https://schema.org/ItemList"
      role="region"
      aria-label="AI-powered medical billing automation services"
    >
      {/* ✅ Add Schemas to Head */}
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(itemListSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(serviceCatalogSchema)}
        </script>
      </Helmet>

      {/* ✅ SEO-OPTIMIZED H2 */}
      <motion.h2
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        viewport={{ once: true }}
        style={{
          fontSize: 'clamp(1.8rem, 4vw, 2.5rem)',
          fontWeight: '700',
          marginBottom: '20px',
          color: '#1A1A1A',
          lineHeight: '1.3',
        }}
        itemProp="name"
      >
        AI-Powered Medical Billing Outsourcing Services
      </motion.h2>

      {/* Supporting paragraph with keywords and internal links */}
      <p style={{
        fontSize: '1.1rem',
        maxWidth: '900px',
        margin: '0 auto 50px',
        lineHeight: '1.7',
        color: '#555',
      }}
      itemProp="description"
      >
        Our{' '}
        <Link 
          to="/why" 
          style={{ color: '#0077b6', textDecoration: 'underline', fontWeight: '600' }}
          aria-label="Learn about healthcare revenue cycle management services"
        >
          healthcare revenue cycle management services
        </Link>{' '}
        leverage AI agents to automate every aspect of your billing workflow. Experience better patient satisfaction with faster, more accurate processing through our{' '}
        <Link 
          to="/healthcare/ai" 
          style={{ color: '#0077b6', textDecoration: 'underline', fontWeight: '600' }}
          aria-label="Explore AI-powered automation solutions"
        >
          AI-powered automation
        </Link>.
      </p>

      <div
        style={{
          overflow: 'hidden',
          position: 'relative',
          background: '#fff',
          zIndex: 2,
          padding: '20px 0'
        }}
        role="list"
        aria-label="Medical billing automation services carousel"
      >
        <div className="scroll-carousel">
          {[...items, ...items].map((item, index) => (
            <Link
              key={index}
              to={item.link}
              style={{
                textDecoration: 'none',
                color: 'inherit',
              }}
              aria-label={`Learn more about ${item.label} - ${item.description}`}
              role="listitem"
            >
              <motion.div
                initial={{ opacity: 0, rotateY: -10 }}
                whileInView={{ opacity: 0.9, rotateY: 0 }}
                transition={{ duration: 0.6, delay: (index % items.length) * 0.1 }}
                viewport={{ once: true }}
                style={{
                  flex: '0 0 auto',
                  width: '200px',
                  height: '180px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 500,
                  fontSize: '0.98rem',
                  color: '#0077b6',
                  transition: 'all 0.3s ease',
                  background: '#f9f9f9',
                  borderRadius: '12px',
                  boxShadow: '0 6px 14px rgba(0, 0, 0, 0.06)',
                  transformStyle: 'preserve-3d',
                  padding: '15px',
                  textAlign: 'center',
                  cursor: 'pointer',
                }}
                whileHover={{
                  scale: 1.05,
                  rotateY: 5,
                  boxShadow: '0 12px 24px rgba(0,0,0,0.15)',
                  background: '#e8f5e9',
                }}
                itemProp="itemListElement"
                itemScope
                itemType="https://schema.org/ListItem"
              >
                <meta itemProp="position" content={(index % items.length) + 1} />
                
                <div 
                  style={{ fontSize: '2.5rem', marginBottom: '12px' }}
                  aria-hidden="true"
                >
                  {item.icon}
                </div>
                
                <div style={{ 
                  fontWeight: '600',
                  marginBottom: '6px',
                  fontSize: '1rem'
                }}
                itemProp="name"
                >
                  {item.label}
                </div>
                
                <div style={{ 
                  fontSize: '0.8rem', 
                  color: '#666',
                  lineHeight: '1.4'
                }}
                itemProp="description"
                >
                  {item.description}
                </div>

                {/* Hover indicator */}
                <div style={{
                  fontSize: '0.75rem',
                  color: '#0077b6',
                  marginTop: '8px',
                  fontWeight: '600',
                  opacity: 0,
                  transition: 'opacity 0.3s ease',
                }}
                className="hover-indicator"
                >
                  Learn More →
                </div>
              </motion.div>
            </Link>
          ))}
        </div>
      </div>

      {/* Additional benefit statement with internal links */}
      <p style={{
        fontSize: '1.05rem',
        marginTop: '40px',
        color: '#666',
        fontStyle: 'italic',
      }}>
        Trusted by hospitals and clinics for comprehensive{' '}
        <Link 
          to="/why" 
          style={{ color: '#0077b6', textDecoration: 'underline' }}
        >
          revenue cycle management services
        </Link>.{' '}
        See how our{' '}
        <Link 
          to="/healthcare/ai" 
          style={{ color: '#0077b6', textDecoration: 'underline' }}
        >
          AI automation
        </Link>{' '}
        delivers results.
      </p>

      {/* Quick Links Section */}
      <div style={{
        marginTop: '50px',
        padding: '30px',
        backgroundColor: '#f8f9fa',
        borderRadius: '12px',
        maxWidth: '1000px',
        margin: '50px auto 0',
      }}>
        <h3 style={{
          fontSize: '1.3rem',
          fontWeight: '700',
          color: '#0077b6',
          marginBottom: '20px',
        }}>
          🔗 Explore Our Most Popular Services
        </h3>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '15px',
        }}>
          <Link 
            to="/healthcare/ai/eligibility-check"
            style={{
              padding: '15px',
              backgroundColor: '#ffffff',
              borderRadius: '8px',
              textDecoration: 'none',
              color: '#0077b6',
              fontSize: '0.95rem',
              fontWeight: '600',
              boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
              transition: 'all 0.3s ease',
              display: 'block',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#e8f5e9';
              e.currentTarget.style.transform = 'translateY(-2px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = '#ffffff';
              e.currentTarget.style.transform = 'translateY(0)';
            }}
          >
            🛡️ Eligibility Verification
          </Link>
          <Link 
            to="/healthcare/ai/denial-management"
            style={{
              padding: '15px',
              backgroundColor: '#ffffff',
              borderRadius: '8px',
              textDecoration: 'none',
              color: '#0077b6',
              fontSize: '0.95rem',
              fontWeight: '600',
              boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
              transition: 'all 0.3s ease',
              display: 'block',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#e8f5e9';
              e.currentTarget.style.transform = 'translateY(-2px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = '#ffffff';
              e.currentTarget.style.transform = 'translateY(0)';
            }}
          >
            🧠 Denial Management
          </Link>
          <Link 
            to="/healthcare/ai/payment-posting"
            style={{
              padding: '15px',
              backgroundColor: '#ffffff',
              borderRadius: '8px',
              textDecoration: 'none',
              color: '#0077b6',
              fontSize: '0.95rem',
              fontWeight: '600',
              boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
              transition: 'all 0.3s ease',
              display: 'block',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#e8f5e9';
              e.currentTarget.style.transform = 'translateY(-2px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = '#ffffff';
              e.currentTarget.style.transform = 'translateY(0)';
            }}
          >
            📄 Payment Posting
          </Link>
          <Link 
            to="/healthcare/ai/patient-intake"
            style={{
              padding: '15px',
              backgroundColor: '#ffffff',
              borderRadius: '8px',
              textDecoration: 'none',
              color: '#0077b6',
              fontSize: '0.95rem',
              fontWeight: '600',
              boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
              transition: 'all 0.3s ease',
              display: 'block',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#e8f5e9';
              e.currentTarget.style.transform = 'translateY(-2px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = '#ffffff';
              e.currentTarget.style.transform = 'translateY(0)';
            }}
          >
            🧾 Patient Intake
          </Link>
        </div>
      </div>

      {/* Mobile Responsive Styles */}
      <style>{`
        @keyframes scroll {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }

        .scroll-carousel {
          display: flex;
          gap: 40px;
          animation: scroll 100s linear infinite;
          min-width: 100%;
          max-width: 100%;
        }

        .scroll-carousel:hover {
          animation-play-state: paused;
        }

        /* Show "Learn More" on hover */
        .scroll-carousel > a:hover .hover-indicator {
          opacity: 1 !important;
        }

        @media (max-width: 768px) {
          section {
            padding: 60px 2%;
          }

          div[style*='width: 200px'] {
            width: 150px !important;
            height: 150px !important;
            font-size: 0.9rem !important;
          }

          .scroll-carousel {
            animation-duration: 40s !important;
          }

          h2 {
            font-size: 1.6rem !important;
          }

          p {
            font-size: 0.95rem !important;
            padding: 0 10px;
          }
        }
      `}</style>
    </section>
  );
};

export default PatientExperienceCarousel;